import { Box, Chip, Typography } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  timelineItemClasses,
} from '@mui/lab';
import { useTranslation } from 'react-i18next';
import type { ProgramPhase } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';

export interface ProjectProgramTimelineProps {
  phases: ProgramPhase[];
}

/**
 * Vertical timeline of the program phases a project has been through.
 *
 * Sits in the right column of the Overview tab. The last phase has no
 * `endDate` — it's the current ongoing phase and gets the brand red dot +
 * an "Ongoing" label. Past phases get a neutral gray dot and the date
 * range.
 *
 * The container scrolls independently (`maxHeight: calc(100vh - 320px)`)
 * so a long history never pushes the rest of the page off-screen.
 */
export default function ProjectProgramTimeline({ phases }: ProjectProgramTimelineProps) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="project-program-timeline"
      sx={{ p: 0 }}
    >
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('projects.detail.programTimeline')}
      </Typography>

      {phases.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('projects.detail.noProgramHistory')}
        </Typography>
      ) : (
        <Box
          sx={{
            maxHeight: 'calc(100vh - 320px)',
            overflowY: 'auto',
            // Subtle scrollbar styling to match the brand gray
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.lightGray,
              borderRadius: 3,
            },
          }}
        >
          <Timeline
            position="right"
            sx={{
              p: 0,
              m: 0,
              // Kill the default left padding caused by the opposite-content slot
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {phases.map((phase, idx) => {
              const isLast = idx === phases.length - 1;
              const isOngoing = !phase.endDate;
              const start = phase.startDate?.toDate?.().toLocaleDateString() ?? '—';
              const end = phase.endDate?.toDate?.().toLocaleDateString();

              return (
                <TimelineItem key={idx}>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: isOngoing ? colors.red : colors.lightGray,
                        boxShadow: 'none',
                        m: 0,
                      }}
                    />
                    {!isLast && <TimelineConnector sx={{ backgroundColor: colors.lightGray }} />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pb: 2, pr: 0 }}>
                    <Chip
                      label={phase.type}
                      size="small"
                      sx={{ borderRadius: '6px', mb: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {isOngoing
                        ? `${start} → ${t('projects.detail.programOngoing')}`
                        : `${start} → ${end ?? '—'}`}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </Box>
      )}
    </Box>
  );
}
